import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import moment from 'moment'
import API from '../../../services/API'
import useIsMobile from '../../../helpers/useIsMobile'
import { GlobalContext } from '../../../App'
import Age from '../../../components/Age'
import EmployeeSelector from '../../events/EmployeeSelector'
import MFALogin from '../../../helpers/auth/MFALogin'

const fullName = (person) =>
  person ? `${person.firstName} ${person.lastName}` : 'Unknown'

const patientLabel = (record) => {
  const demographics = record?.demographics
  if (!demographics?.firstName && !demographics?.lastName) return 'Unknown patient'

  const age = demographics?.dob
    ? ` (${Age(demographics.dob, record?.createdAt)})`
    : ''
  return `${demographics?.firstName ?? ''} ${demographics?.lastName ?? ''}${age}`.trim()
}

// Why a looked-up record can't be audited by hand, if it can't.
const blockedReason = (record) => {
  if (record.existingAudit?.status === 'Completed') return 'Already audited'
  if (record.review?.status !== 'Submitted') return 'Not submitted yet'
  return null
}

// Governance raises an audit on any submitted record by entering its ePCR
// number. `onRaised(auditID)` fires once the audit exists. Opened from a
// record (`record` = { _id, epcrNumber }) it skips the search and shows just
// that record.
const RaiseAuditDialog = ({ open, onClose, onRaised, record: fromRecord }) => {
  const { setToast } = useContext(GlobalContext)
  const isPhone = useIsMobile('sm')
  const [epcrNumber, setEpcrNumber] = useState('')
  const [records, setRecords] = useState(null)
  const [selected, setSelected] = useState(null)
  const [note, setNote] = useState('')
  const [otherClinician, setOtherClinician] = useState(false)
  const [clinician, setClinician] = useState(null)
  const [searching, setSearching] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [requiresMFA, setRequiresMFA] = useState(false)

  useEffect(() => {
    if (!open) return
    setRequiresMFA(false)
    setEpcrNumber(fromRecord?.epcrNumber ?? '')
    setRecords(null)
    setSelected(null)
    setNote('')
    setOtherClinician(false)
    setClinician(null)
    setError(null)
    if (fromRecord?.epcrNumber) handleSearch(fromRecord.epcrNumber)
  }, [open])

  const handleSearch = (number = epcrNumber) => {
    if (!String(number).trim()) return
    setSearching(true)
    setError(null)
    setSelected(null)
    API.get('/epcr-audits/lookup', { params: { epcrNumber: number } })
      .then((res) => {
        if (res.status === 200) {
          setRequiresMFA(false)
          // Numbers aren't guaranteed unique - keep only the record we came from
          const found = (res.data.records ?? []).filter(
            (record) => !fromRecord || record._id === fromRecord._id,
          )
          setRecords(found)
          const raisable = found.filter((record) => !blockedReason(record))
          if (found.length === 1 && raisable.length === 1) setSelected(found[0])
        } else if (res.status === 403) {
          setRequiresMFA(true)
        } else {
          setRecords(null)
          setError(res.data?.error ?? 'Unable to look up that record')
        }
      })
      .finally(() => setSearching(false))
  }

  const handleRaise = () => {
    setSaving(true)
    setError(null)
    API.post('/epcr-audits', {
      epcr: selected._id,
      note,
      clinician: otherClinician && clinician ? clinician._id : null,
    })
      .then((res) => {
        if (res.status === 200) {
          setToast({
            message: res.data?.alreadyRequired
              ? 'This record was already awaiting audit - updated'
              : 'Audit raised',
          })
          onRaised(res.data?._id)
        } else if (res.status === 403) {
          setRequiresMFA(true)
        } else {
          setError(res.data?.error ?? 'Unable to raise this audit')
        }
      })
      .finally(() => setSaving(false))
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isPhone}
    >
      <DialogTitle>Raise a Record Audit</DialogTitle>
      <DialogContent>
        <Stack gap={2} pt={1}>
          <Typography variant="body2" color="text.secondary">
            Audit any submitted record, whether or not it matched your audit
            criteria.
          </Typography>
          {requiresMFA && (
            <MFALogin
              onSuccess={() => {
                setRequiresMFA(false)
                handleSearch()
              }}
            />
          )}
          {!fromRecord && (
          <Stack direction="row" gap={1}>
            <TextField
              label="ePCR number"
              placeholder="e.g. 0247"
              size="small"
              fullWidth
              autoFocus
              value={epcrNumber}
              onChange={(e) => setEpcrNumber(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleSearch()
                }
              }}
            />
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              disabled={!epcrNumber.trim() || searching}
              onClick={() => handleSearch()}
            >
              Find
            </Button>
          </Stack>
          )}

          {records?.length === 0 && (
            <Alert severity="info">No record found with that ePCR number.</Alert>
          )}

          {records?.map((record) => {
            const blocked = blockedReason(record)
            const isSelected = selected?._id === record._id
            return (
              <Paper
                key={record._id}
                variant="outlined"
                onClick={() => !blocked && setSelected(record)}
                sx={{
                  p: 1.5,
                  cursor: blocked ? 'default' : 'pointer',
                  opacity: blocked ? 0.6 : 1,
                  borderColor: isSelected ? 'primary.main' : undefined,
                  borderWidth: isSelected ? 2 : 1,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  gap={1}
                >
                  <Box>
                    <Typography variant="subtitle2">
                      ePCR {record.epcrNumber} — {patientLabel(record)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {moment(
                        record.incident?.incidentDate ?? record.createdAt,
                      ).format('DD/MM/YYYY')}
                      {' · '}
                      {fullName(record.owner)}
                      {record.mode && record.mode !== 'ePCR'
                        ? ` · ${record.mode}`
                        : ''}
                    </Typography>
                  </Box>
                  {blocked ? (
                    <Chip size="small" label={blocked} sx={{ borderRadius: 1 }} />
                  ) : record.existingAudit ? (
                    <Chip
                      size="small"
                      color="warning"
                      label="Awaiting audit"
                      sx={{ borderRadius: 1 }}
                    />
                  ) : null}
                </Stack>
              </Paper>
            )
          })}

          {selected && (
            <>
              <TextField
                label="Reason for audit (optional)"
                placeholder="e.g. Complaint received, learning case"
                multiline
                minRows={2}
                fullWidth
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={otherClinician}
                    onChange={(e) => setOtherClinician(e.target.checked)}
                  />
                }
                label={`Audit a clinician other than ${fullName(selected.owner)}`}
              />
              {otherClinician && (
                <EmployeeSelector
                  value={clinician}
                  onChange={(e, value) => setClinician(value)}
                  label="Clinician being audited"
                  fullWidth
                />
              )}
            </>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selected || saving || (otherClinician && !clinician)}
          onClick={handleRaise}
        >
          Raise Audit
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RaiseAuditDialog
